<script setup lang="ts">
import { QuillEditor } from '@vueup/vue-quill'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

import { useField, useForm } from 'vee-validate'
import * as yup from 'yup'
import { createPostAction } from '@/api/functions/settings/blogs-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import ErrorIcon from '/svg/error-icon.svg'

const router = useRouter()
const toast = useToast()

// --- Validation Schema ---
const schema = yup.object({
  title: yup.string().required('عنوان مطلب الزامی است'),
  excerpt: yup.string().required('محتوای مطلب الزامی است'),
  imageFile: yup.mixed().nullable(),
})

// --- Vee-validate Form ---
const { handleSubmit, errors } = useForm({
  validationSchema: schema,
  initialValues: {
    title: '',
    excerpt: '',
    imageFile: null,
  },
})

const { value: title } = useField('title')
const { value: excerpt } = useField<string>('excerpt')
const { value: imageFile } = useField('imageFile')

// UI image preview
const img_Url = ref('')
const fileInput = ref<HTMLInputElement | null>(null)

// local excerpt empty-html flag (like edit version)
const excerptError = ref(false)

// --- Quill Toolbar ---
const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ color: [] }, { background: [] }],
  ['clean'],
]

// --- Handle file select ---
const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]
    img_Url.value = URL.createObjectURL(file)
    imageFile.value = file
  }
}

const triggerFileUpload = () => fileInput.value?.click()

// --- Mutation for create ---
const createMutation = useMutationApi({
  mutationFn: () =>
    createPostAction({
      title: title.value as string,
      content: excerpt.value as string,
      image: imageFile.value ?? undefined,
    }),
  onSuccess: () => {
    toast.success('مطلب با موفقیت ایجاد شد')
    router.push('/settings/blog')
  },
  onError: (err: unknown) => {
    toast.error(getErrorMessage(err))
  },
})

// --- Submit Handler ---
// important: check for Quill empty html "<p><br></p>" like edit version
const onSubmitClick = handleSubmit(() => {
  if (excerpt.value === '<p><br></p>') {
    excerptError.value = true
    return
  }
  excerptError.value = false
  createMutation.mutate()
})
</script>

<template>
  <div class="mb-9">
    <router-link to="/settings/blog"
      class="w-fit mb-6 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
      <img src="/svg/back-icon.svg" class="me-1" width="22" />
      <span>بازگشت</span>
    </router-link>

    <div class=" flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-0 mb-9">
      <div class="text-[#1b325a] text-[22px]">
        ایجاد مطلب جدید
      </div>

      <!-- actions  -->
      <div class="w-full lg:w-auto flex flex-row items-center gap-2">
        <Button :isAdmin="true" :loading="createMutation.isPending.value" :disabled="createMutation.isPending.value"
          @click="onSubmitClick" class=" w-1/2 lg:w-auto px-2  rounded-[10px]  h-10! py-0 text-[13px] font-normal ">
          <span class="text-nowrap flex items-center  justify-center gap-2 ">
            بارگذاری مطلب
          </span>
        </Button>
      </div>
    </div>

    <!-- TITLE -->
    <div class="w-full flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-24 lg:h-28 p-[26px] bg-white rounded-xl">
      <span class="text-[#1b325a] text-sm text-nowrap">عنوان مطلب</span>
      <Input v-model="title" name="title" type="text" class="h-[50px]!" :error="errors.title" />
    </div>

    <!-- IMAGE -->
    <div class="w-full flex flex-col lg:flex-row  lg:items-center gap-6 lg:gap-24  p-[26px] bg-white rounded-xl my-4">
      <span class="text-[#1b325a] text-sm">تصویر نمایه</span>

      <div class="flex flex-col gap-4">
        <div v-if="!img_Url" class="w-full lg:w-[360px] h-[211px] rounded-xl bg-[#f5f5f5] object-cover"></div>

        <img v-else :src="img_Url" alt="blog-img"
          class="w-full lg:w-[360px] h-[211px] rounded-xl border border-[#000000de] object-cover" />

        <Button @click="triggerFileUpload" :isAdmin="true" variant="outline"
          class="w-fit px-3.5! border-2 rounded-lg! h-8! py-0 text-[13px] font-normal">
          <span class="flex items-center justify-center gap-2">
            {{ !img_Url ? 'بارگذاری' : 'بارگذاری دوباره' }}
          </span>
        </Button>

        <input type="file" accept="image/*" class="hidden" ref="fileInput" @change="handleFileChange" />

        <div v-if="errors.imageFile" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b]">
          <img :src="ErrorIcon" width="14" alt="error-icon" />
          {{ errors.imageFile }}
        </div>
      </div>
    </div>

    <!-- CONTENT -->
    <!-- IMPORTANT: bind Quill editor content with v-model:content so excerpt (useField) updates -->
    <QuillEditor v-model:content="excerpt" theme="snow" :toolbar="toolbarOptions"
      class="bg-white border-0! min-h-[340px] rounded-b-xl text-right" placeholder="شروع به نوشتن کنید"
      content-type="html" />

    <div v-if="excerptError" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
      <img :src="ErrorIcon" width="16" alt="error-icon" />
      این فیلد ضروری است
    </div>

    <div v-if="errors.excerpt && !excerptError"
      class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
      <img :src="ErrorIcon" width="14" alt="error-icon" />
      {{ errors.excerpt }}
    </div>
  </div>
</template>

<style>
@media (min-width: 1025px) {
  .ql-toolbar.ql-snow {
    height: 46px;
  }
}

.ql-toolbar.ql-snow {
  background-color: white;
  border-color: #eaeef5;
  border-top: none;
  border-inline: none;
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
}

.ql-toolbar {
  direction: ltr !important;
  text-align: left !important;
}

.ql-editor {
  direction: rtl;
  text-align: right;
  font-family: inherit;
}
</style>
